import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../../config/ApiUrl";

const WT = API_URL.WHATSAPP_TEMPLATES;

// Admin (SuperAdmin) CRUD over the curated global (master) WhatsApp template catalog.

export const getMasterTemplates = createAsyncThunk(
  "whatsAppTemplates/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WT.LIST);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createMasterTemplate = createAsyncThunk(
  "whatsAppTemplates/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WT.LIST, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateMasterTemplate = createAsyncThunk(
  "whatsAppTemplates/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      await axios.put(WT.BY_ID(id), payload);
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setMasterTemplateActive = createAsyncThunk(
  "whatsAppTemplates/setActive",
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      await axios.put(WT.SET_ACTIVE(id, isActive));
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteMasterTemplate = createAsyncThunk(
  "whatsAppTemplates/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(WT.BY_ID(id));
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Provision one master onto every connected tenant's WABA (create + sync updates to Meta).
export const provisionMasterToAll = createAsyncThunk(
  "whatsAppTemplates/provisionToAll",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WT.PROVISION(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Clear tenant overrides for a master and re-apply master content everywhere.
export const resetMasterOverrides = createAsyncThunk(
  "whatsAppTemplates/resetOverrides",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WT.RESET_OVERRIDES(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const whatsAppTemplatesSlice = createSlice({
  name: "whatsAppTemplates",
  initialState: {
    templates: [],
    isLoading: false,
    isSaving: false,
    isProvisioning: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMasterTemplates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMasterTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload || [];
      })
      .addCase(getMasterTemplates.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createMasterTemplate.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createMasterTemplate.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(createMasterTemplate.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(updateMasterTemplate.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateMasterTemplate.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(updateMasterTemplate.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(provisionMasterToAll.pending, (state) => {
        state.isProvisioning = true;
      })
      .addCase(provisionMasterToAll.fulfilled, (state) => {
        state.isProvisioning = false;
      })
      .addCase(provisionMasterToAll.rejected, (state) => {
        state.isProvisioning = false;
      })
      .addCase(resetMasterOverrides.pending, (state) => {
        state.isProvisioning = true;
      })
      .addCase(resetMasterOverrides.fulfilled, (state) => {
        state.isProvisioning = false;
      })
      .addCase(resetMasterOverrides.rejected, (state) => {
        state.isProvisioning = false;
      });
  },
});

export default whatsAppTemplatesSlice.reducer;
